import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ShoppingBag, BookOpen, Search, X, Menu,
  ChevronDown, Library, Store, Package, Home,
  Sun, Moon, LogOut, Settings,
} from "lucide-react";
import { useCart } from "./CartContext";
import EbenezerLogo from "./EbenezerLogo";

/* Nav links shown on desktop (Home removed — logo handles it) */
const NAV_LINKS = [
  { label: "Library", path: "/search",     icon: Library },
  { label: "Store",   path: "/store",      icon: Store   },
  { label: "My Shelf",path: "/my-library", icon: BookOpen },
];

export default function Navbar({ books, theme, toggleTheme }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { itemCount, openCart } = useCart();

  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileOpen,   setMobileOpen]   = useState(false);
  const [searchOpen,   setSearchOpen]   = useState(false);
  const [searchQuery,  setSearchQuery]  = useState("");
  const [scrolled,     setScrolled]     = useState(false);
  const searchRef  = useRef(null);
  const dropdownRef = useRef(null);

  const urlSearch = useMemo(() => new URLSearchParams(location.search).get("q") || "", [location.search]);
  useEffect(() => { setSearchQuery(urlSearch); }, [urlSearch]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const fn = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  useEffect(() => {
    if (searchOpen) setTimeout(() => searchRef.current?.focus(), 60);
  }, [searchOpen]);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setShowDropdown(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const libraryCount = useMemo(() => {
    if (!books?.length) return 0;
    return books.filter(b => localStorage.getItem(`bookmark_${b.id}`)).length;
  }, [books]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setMobileOpen(false);
    }
  };

  const isActive = (path) => path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
  const userInitial = user?.displayName?.[0] || user?.email?.[0] || "U";

  /* ── Shared icon button style ── */
  const iconBtn = (active = false) => ({
    width: "36px", height: "36px",
    borderRadius: "var(--radius-full)",
    border: `1px solid ${active ? "var(--gold)" : "var(--dust)"}`,
    background: active ? "var(--gold-faint)" : "var(--cream-2)",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: active ? "var(--gold)" : "var(--muted)",
    cursor: "pointer", transition: "all 0.18s", flexShrink: 0,
    position: "relative",
  });

  return (
    <>
      {/* ════════════════════════════════ NAV BAR ════════════════════════════════ */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 200,
        background: "var(--nav-bg)",
        borderBottom: scrolled ? "1px solid var(--nav-border)" : "1px solid transparent",
        backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
        boxShadow: scrolled ? "0 4px 24px rgba(0,0,0,0.08)" : "none",
        transition: "box-shadow 0.3s, border-color 0.3s",
        height: "var(--nav-h)",
      }}>
        <div style={{
          maxWidth: "var(--max-w)", margin: "0 auto",
          padding: "0 1.25rem", height: "100%",
          display: "flex", alignItems: "center", gap: "1rem",
        }}>

          {/* ── LOGO ── */}
          <div
            onClick={() => navigate("/")}
            className="eb-logo-wrap"
            style={{ display: "flex", alignItems: "center", gap: "9px", cursor: "pointer", flexShrink: 0, userSelect: "none" }}
          >
            <EbenezerLogo height={54} theme={theme} className="eb-logo-img" />
            <div>
              <div style={{
                fontFamily: "var(--font-display)", fontSize: "1.4rem",
                fontWeight: 700, color: "var(--nav-text)", lineHeight: 1,
                letterSpacing: "0.01em", whiteSpace: "nowrap",
              }}>Ebenezer Books</div>
              <div style={{ fontSize: "0.65rem", color: "var(--muted)", letterSpacing: "0.14em", textTransform: "uppercase", lineHeight: 1, marginTop: "3px" }}>
                Library &amp; Store
              </div>
            </div>
          </div>

          {/* ── DESKTOP NAV LINKS ── */}
          <div className="eb-nav-links" style={{ display: "flex", alignItems: "center", gap: "2px", flex: 1 }}>
            {NAV_LINKS.map(({ label, path, icon: Icon }) => {
              const active = isActive(path);
              return (
                <button key={path} onClick={() => navigate(path)} style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "7px 15px", borderRadius: "var(--radius-full)",
                  border: "none",
                  background: active ? "var(--gold-faint-2)" : "none",
                  color: active ? "var(--gold)" : "var(--muted)",
                  fontSize: "0.92rem", fontWeight: active ? 600 : 500,
                  cursor: "pointer", transition: "all 0.18s", whiteSpace: "nowrap",
                }}
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.background = "var(--gold-faint)"; e.currentTarget.style.color = "var(--ink)"; } }}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "var(--muted)"; } }}
                >
                  <Icon size={13} strokeWidth={2} />
                  {label}
                  {label === "My Shelf" && libraryCount > 0 && (
                    <span style={{ background: "var(--gold)", color: "#fff", borderRadius: "99px", padding: "1px 5px", fontSize: "0.58rem", fontWeight: 700 }}>{libraryCount}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── RIGHT ACTIONS ── */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "auto", flexShrink: 0 }}>

            {/* Search — desktop only */}
            <button
              className="eb-desktop-only"
              onClick={() => setSearchOpen(true)}
              style={iconBtn()}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--gold)"; e.currentTarget.style.color = "var(--gold)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--dust)"; e.currentTarget.style.color = "var(--muted)"; }}
              title="Search"
            >
              <Search size={15} />
            </button>

            {/* Cart — always visible */}
            <button
              onClick={openCart}
              style={iconBtn()}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--gold)"; e.currentTarget.style.color = "var(--gold)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--dust)"; e.currentTarget.style.color = "var(--muted)"; }}
              title="Cart"
            >
              <ShoppingBag size={15} />
              {itemCount > 0 && (
                <span style={{
                  position: "absolute", top: "-4px", right: "-4px",
                  background: "var(--rust)", color: "#fff",
                  borderRadius: "99px", width: "16px", height: "16px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "0.58rem", fontWeight: 700, border: "2px solid var(--cream)",
                }}>{itemCount}</span>
              )}
            </button>

            {/* Theme toggle — desktop only */}
            <div className="eb-desktop-only" style={{
              display: "flex", background: "var(--cream-2)",
              border: "1px solid var(--dust)", borderRadius: "99px", padding: "3px", gap: "2px",
            }}>
              {[{ k: "light", icon: <Sun size={12} /> }, { k: "dark", icon: <Moon size={12} /> }].map(({ k, icon }) => (
                <button key={k} onClick={() => { if (theme !== k) toggleTheme(); }} style={{
                  width: "26px", height: "26px", borderRadius: "99px", border: "none",
                  background: theme === k ? "var(--gold)" : "none",
                  color: theme === k ? "#fff" : "var(--muted)",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.18s",
                }}>{icon}</button>
              ))}
            </div>

            {/* User avatar — desktop dropdown */}
            {user ? (
              <div className="eb-desktop-only" style={{ position: "relative" }} ref={dropdownRef}>
                <button onClick={() => setShowDropdown(!showDropdown)} style={{
                  display: "flex", alignItems: "center", gap: "0",
                  background: "var(--cream-2)",
                  border: `1px solid ${showDropdown ? "var(--gold)" : "var(--dust)"}`,
                  borderRadius: "99px", padding: "3px",
                  cursor: "pointer", transition: "all 0.18s",
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "var(--gold)"}
                  onMouseLeave={e => { if (!showDropdown) e.currentTarget.style.borderColor = "var(--dust)"; }}
                >
                  <div style={{
                    width: "28px", height: "28px", borderRadius: "50%",
                    background: "linear-gradient(135deg, var(--gold), var(--gold-light))",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    overflow: "hidden",
                  }}>
                    {user.photoURL
                      ? <img src={user.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span style={{ color: "#fff", fontSize: "0.68rem", fontWeight: 700 }}>{userInitial.toUpperCase()}</span>
                    }
                  </div>
                </button>

                {showDropdown && (
                  <div style={{
                    position: "absolute", right: 0, top: "calc(100% + 8px)",
                    background: "var(--card-bg)", border: "1px solid var(--dust)",
                    borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-lg)",
                    padding: "8px", minWidth: "185px", zIndex: 300,
                    animation: "ddIn 0.15s ease both", transformOrigin: "top right",
                  }}>
                    <div style={{ padding: "6px 10px 10px", borderBottom: "1px solid var(--dust)", marginBottom: "6px" }}>
                      <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--ink)", marginBottom: "2px" }}>
                        {user.displayName || user.email?.split("@")[0]}
                      </div>
                      <div style={{ fontSize: "0.7rem", color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis" }}>{user.email}</div>
                    </div>
                    {[
                      { label: "My Orders", path: "/orders",      Icon: Package  },
                      { label: "My Shelf",  path: "/my-library",  Icon: Library  },
                      ...(isAdmin ? [{ label: "Admin", path: "/admin", Icon: Settings }] : []),
                    ].map(({ label, path, Icon }) => (
                      <button key={path} onClick={() => { setShowDropdown(false); navigate(path); }} style={{
                        width: "100%", display: "flex", alignItems: "center", gap: "8px",
                        padding: "7px 10px", border: "none", borderRadius: "var(--radius)",
                        background: "none", color: "var(--ink)", fontSize: "0.82rem",
                        cursor: "pointer", textAlign: "left", transition: "background 0.15s",
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = "var(--gold-faint)"}
                        onMouseLeave={e => e.currentTarget.style.background = "none"}
                      >
                        <Icon size={13} color="var(--muted)" /> {label}
                      </button>
                    ))}
                    <div style={{ borderTop: "1px solid var(--dust)", marginTop: "6px", paddingTop: "6px" }}>
                      <button onClick={() => { setShowDropdown(false); logout(); }} style={{
                        width: "100%", display: "flex", alignItems: "center", gap: "8px",
                        padding: "7px 10px", border: "none", borderRadius: "var(--radius)",
                        background: "none", color: "var(--rust)", fontSize: "0.82rem",
                        cursor: "pointer", textAlign: "left", transition: "background 0.15s",
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = "var(--rust-faint)"}
                        onMouseLeave={e => e.currentTarget.style.background = "none"}
                      >
                        <LogOut size={13} /> Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button className="eb-desktop-only" onClick={() => navigate("/login")} style={{
                padding: "7px 16px", background: "linear-gradient(135deg, var(--gold), var(--gold-2))",
                border: "none", borderRadius: "99px", color: "#fff",
                fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
                boxShadow: "var(--shadow-gold)", whiteSpace: "nowrap",
              }}>Sign In</button>
            )}

            {/* Hamburger — mobile only */}
            <button
              className="eb-mobile-only"
              onClick={() => setMobileOpen(!mobileOpen)}
              style={{
                ...iconBtn(mobileOpen),
                border: mobileOpen ? "1px solid var(--gold)" : "1px solid var(--dust)",
                background: mobileOpen ? "var(--gold-faint)" : "var(--cream-2)",
                color: mobileOpen ? "var(--gold)" : "var(--muted)",
              }}
            >
              {mobileOpen ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </div>
      </nav>

      {/* ════════════════════════════ MOBILE MENU ════════════════════════════ */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setMobileOpen(false)}
            style={{
              position: "fixed", inset: 0, top: "var(--nav-h)",
              background: "rgba(0,0,0,0.4)", zIndex: 198,
              animation: "fadeIn 0.2s ease",
            }}
          />

          {/* Panel */}
          <div style={{
            position: "fixed", top: "var(--nav-h)", left: 0, right: 0,
            background: "var(--nav-bg)",
            backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
            borderBottom: "1px solid var(--nav-border)",
            boxShadow: "var(--shadow-lg)",
            zIndex: 199, padding: "1rem 1.25rem 1.5rem",
            animation: "slideDown 0.2s ease",
          }}>

            {/* Search bar */}
            <form onSubmit={handleSearchSubmit} style={{
              display: "flex", gap: "8px", marginBottom: "1rem",
            }}>
              <div style={{
                flex: 1, display: "flex", alignItems: "center", gap: "8px",
                background: "var(--cream-2)", border: "1px solid var(--dust)",
                borderRadius: "99px", padding: "0 14px",
              }}>
                <Search size={14} color="var(--muted)" style={{ flexShrink: 0 }} />
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search books…"
                  style={{
                    flex: 1, border: "none", background: "none", outline: "none",
                    color: "var(--ink)", fontSize: "0.88rem", padding: "9px 0",
                    fontFamily: "var(--font-body)",
                  }}
                />
              </div>
              <button type="submit" style={{
                padding: "0 18px", borderRadius: "99px",
                border: "none", background: "var(--gold)", color: "#fff",
                fontWeight: 600, fontSize: "0.82rem", cursor: "pointer",
                flexShrink: 0,
              }}>Go</button>
            </form>

            {/* Nav links */}
            <div style={{ display: "flex", flexDirection: "column", gap: "2px", marginBottom: "0.75rem" }}>
              {[
                { label: "Home",     path: "/",           icon: Home     },
                { label: "Library",  path: "/search",     icon: Library  },
                { label: "Store",    path: "/store",      icon: Store    },
                { label: "My Shelf", path: "/my-library", icon: BookOpen },
                ...(user ? [{ label: "My Orders", path: "/orders", icon: Package }] : []),
              ].map(({ label, path, icon: Icon }) => (
                <button key={path} onClick={() => navigate(path)} style={{
                  display: "flex", alignItems: "center", gap: "10px",
                  padding: "10px 12px", border: "none", borderRadius: "var(--radius)",
                  background: isActive(path) ? "var(--gold-faint-2)" : "none",
                  color: isActive(path) ? "var(--gold)" : "var(--ink)",
                  fontSize: "0.92rem", fontWeight: isActive(path) ? 600 : 400,
                  cursor: "pointer", textAlign: "left", width: "100%",
                  transition: "background 0.15s",
                }}>
                  <Icon size={16} strokeWidth={isActive(path) ? 2.2 : 1.8} /> {label}
                </button>
              ))}
            </div>

            {/* Bottom row: theme toggle + auth */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              paddingTop: "0.75rem", borderTop: "1px solid var(--dust)",
            }}>
              {/* Theme toggle */}
              <div style={{
                display: "flex", background: "var(--cream-2)",
                border: "1px solid var(--dust)", borderRadius: "99px", padding: "3px", gap: "2px",
              }}>
                {[{ k: "light", icon: <Sun size={13} />, label: "Light" }, { k: "dark", icon: <Moon size={13} />, label: "Dark" }].map(({ k, icon, label }) => (
                  <button key={k} onClick={() => { if (theme !== k) toggleTheme(); }} style={{
                    display: "flex", alignItems: "center", gap: "5px",
                    padding: "5px 12px", borderRadius: "99px", border: "none",
                    background: theme === k ? "var(--gold)" : "none",
                    color: theme === k ? "#fff" : "var(--muted)",
                    fontSize: "0.78rem", fontWeight: 600,
                    cursor: "pointer", transition: "all 0.18s",
                  }}>{icon}{label}</button>
                ))}
              </div>

              {/* Auth */}
              {user ? (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                    <div style={{
                      width: "28px", height: "28px", borderRadius: "50%",
                      background: "linear-gradient(135deg, var(--gold), var(--gold-light))",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      overflow: "hidden", flexShrink: 0,
                    }}>
                      {user.photoURL
                        ? <img src={user.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : <span style={{ color: "#fff", fontSize: "0.68rem", fontWeight: 700 }}>{userInitial.toUpperCase()}</span>
                      }
                    </div>
                    <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--ink)", maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {user.displayName || user.email?.split("@")[0]}
                    </span>
                  </div>
                  <button onClick={() => { setMobileOpen(false); logout(); }} style={{
                    display: "flex", alignItems: "center", gap: "5px",
                    padding: "6px 12px", borderRadius: "99px",
                    border: "1px solid var(--rust-faint)", background: "var(--rust-faint)",
                    color: "var(--rust)", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
                  }}>
                    <LogOut size={12} /> Sign Out
                  </button>
                </div>
              ) : (
                <button onClick={() => { setMobileOpen(false); navigate("/login"); }} style={{
                  padding: "8px 20px", background: "linear-gradient(135deg, var(--gold), var(--gold-2))",
                  border: "none", borderRadius: "99px", color: "#fff",
                  fontSize: "0.85rem", fontWeight: 700, cursor: "pointer",
                  boxShadow: "var(--shadow-gold)",
                }}>Sign In</button>
              )}
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════ SEARCH OVERLAY ════════════════════════════ */}
      {searchOpen && (
        <div onClick={e => { if (e.target === e.currentTarget) setSearchOpen(false); }} style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.55)", zIndex: 500,
          display: "flex", alignItems: "flex-start", justifyContent: "center",
          paddingTop: "80px", animation: "fadeIn 0.15s ease",
        }}>
          <div style={{ width: "100%", maxWidth: "640px", margin: "0 1rem", animation: "scaleIn 0.2s ease" }}>
            <form onSubmit={handleSearchSubmit} style={{
              display: "flex", alignItems: "center", gap: "12px",
              background: "var(--card-bg)", border: "1px solid var(--dust)",
              borderRadius: "var(--radius-xl)", padding: "6px 6px 6px 20px",
              boxShadow: "var(--shadow-xl)",
            }}>
              <Search size={18} color="var(--muted)" style={{ flexShrink: 0 }} />
              <input
                ref={searchRef}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search books, authors, genres…"
                style={{
                  flex: 1, border: "none", background: "none", outline: "none",
                  color: "var(--ink)", fontSize: "1rem", fontFamily: "var(--font-body)",
                }}
              />
              <button type="submit" style={{
                padding: "9px 22px", borderRadius: "var(--radius-lg)",
                border: "none", background: "var(--gold)", color: "#fff",
                fontWeight: 600, fontSize: "0.88rem", flexShrink: 0, cursor: "pointer",
              }}>Search</button>
            </form>
            <div style={{ textAlign: "center", marginTop: "10px", fontSize: "0.76rem", color: "rgba(255,255,255,0.45)" }}>
              Click outside to close
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Desktop */
        @media (min-width: 821px) {
          .eb-nav-links    { display: flex !important; }
          .eb-desktop-only { display: flex !important; }
          .eb-mobile-only  { display: none  !important; }
          .eb-logo-img     { height: 54px !important; width: auto !important; }
        }
        /* Tablet / large mobile */
        @media (max-width: 820px) {
          .eb-nav-links    { display: none  !important; }
          .eb-desktop-only { display: none  !important; }
          .eb-mobile-only  { display: flex  !important; }
          .eb-logo-img     { height: 44px !important; width: auto !important; }
        }
        /* Small phones */
        @media (max-width: 400px) {
          .eb-logo-img { height: 36px !important; width: auto !important; }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes ddIn {
          from { opacity: 0; transform: scale(0.95); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </>
  );
}
